import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { useHubToast } from "@/components/ui/HubToast";
import { Input } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { CardTitleWithHelp } from "@/components/ui/title-help";

import { ApInvoiceExpenseCategoryField } from "./ApInvoiceExpenseCategoryField";
import {
  createApInvoiceUpsSaveQueue,
  requestApInvoiceUpsSave,
  settleApInvoiceUpsSave,
} from "./apInvoiceUpsSaveQueue";
import {
  applyApInvoiceUpsAllocation,
  saveApInvoiceUpsAllocation,
  searchMultiPoTshGipis,
  fetchApInvoiceUps,
  type ApInvoiceRow,
  type UpsInvoiceState,
} from "./apInvoicesApi";

const LINE_HELP =
  "Put the carrier bill on the left with the other invoice documents. AI review reads that PDF or CSV and fills one line per shipment here. Freight from us to a customer stays a non-PO line. Freight to us can attach to a purchase order. RMA and return-to-sender lines stay off the purchase invoice.";

interface DraftLine {
  key: string;
  kind: "gipi" | "expense";
  description: string;
  amount: string;
  purchaseInvoiceId: string;
  purchaseInvoiceLabel: string;
  purchaseOrderHint: string;
  categoryId: string;
  categoryName: string;
  createdInvoiceId: string | null;
}

function money(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function gipiOptionLabel(candidate: {
  mv_number: string | null;
  purchase_order_number: string | null;
  supplier_name: string | null;
}): string {
  const gipi = candidate.mv_number?.trim() || "GIPI";
  const purchaseOrder = candidate.purchase_order_number?.trim() || "";
  const supplier = candidate.supplier_name?.trim() || "";
  return [gipi, purchaseOrder, supplier].filter((part) => part !== "").join(" · ");
}

function LineGipiField(props: {
  purchaseInvoiceId: string;
  purchaseInvoiceLabel: string;
  disabled: boolean;
  onChange: (purchaseInvoiceId: string, label: string) => void;
  onCommit?: () => void;
}): JSX.Element {
  const [search, setSearch] = useState("");
  const query = useQuery({
    queryKey: ["accounting", "multi-po-tsh-gipi", search],
    queryFn: () => searchMultiPoTshGipis(search),
    enabled: !props.disabled && search.trim().length >= 2,
  });
  const options = useMemo(() => {
    const fromSearch = (query.data ?? []).map((row) => ({
      value: String(row.purchase_invoice_id),
      label: gipiOptionLabel(row),
    }));
    if (props.purchaseInvoiceId !== "" && !fromSearch.some((option) => option.value === props.purchaseInvoiceId)) {
      return [
        { value: props.purchaseInvoiceId, label: props.purchaseInvoiceLabel || `GIPI ${props.purchaseInvoiceId}` },
        ...fromSearch,
      ];
    }
    return fromSearch;
  }, [props.purchaseInvoiceId, props.purchaseInvoiceLabel, query.data]);

  return (
    <SearchableSelect
      value={props.purchaseInvoiceId}
      options={options}
      searchValue={search}
      onSearchChange={setSearch}
      onValueChange={(option) => {
        props.onChange(option?.value ?? "", option?.label ?? "");
        props.onCommit?.();
      }}
      placeholder="Search by purchase order"
      searchPlaceholder="PO or GIPI number…"
      emptyMessage={search.trim().length < 2 ? "Type at least 2 characters." : "No GIPI documents match."}
      isLoading={query.isLoading}
      disabled={props.disabled}
    />
  );
}

export function ApInvoiceUpsPanel(props: {
  invoiceId: string;
  formLocked: boolean;
  onInvoiceUpdated: (invoice: ApInvoiceRow) => void;
  submitRef: { current: (() => Promise<ApInvoiceRow>) | null };
}): JSX.Element {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const upsQuery = useQuery({
    queryKey: ["accounting", "ap-invoice-ups", props.invoiceId],
    queryFn: () => fetchApInvoiceUps(props.invoiceId),
  });
  const saved = upsQuery.data;
  const [invoiceTotal, setInvoiceTotal] = useState<string | null>(null);
  const [rows, setRows] = useState<DraftLine[] | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [categoryName, setCategoryName] = useState<string | null>(null);

  const applied = saved?.applied_at != null;
  const totalText = invoiceTotal ?? saved?.invoice_total ?? "";
  const savedExplicit = (saved?.lines ?? []).filter((line) => line.kind !== "remainder");
  const savedRemainder = (saved?.lines ?? []).find((line) => line.kind === "remainder");
  const draftRows =
    rows ??
    savedExplicit.map((line) => ({
      key: line.id,
      kind: line.kind === "gipi" ? "gipi" as const : "expense" as const,
      description: line.description ?? "",
      amount: line.amount,
      purchaseInvoiceId: line.purchase_invoice_id ?? "",
      purchaseInvoiceLabel:
        [line.purchase_invoice_mv_number, line.purchase_order_number].filter((part) => (part ?? "").trim() !== "").join(" · "),
      purchaseOrderHint: line.purchase_order_number?.trim() || "",
      categoryId: line.qbo_expense_account_id ?? "",
      categoryName: line.qbo_expense_account_name ?? "",
      createdInvoiceId: line.created_accounts_payable_invoice_id,
    }));
  const accountId = categoryId ?? savedRemainder?.qbo_expense_account_id ?? saved?.qbo_expense_account_id ?? null;
  const accountName = categoryName ?? savedRemainder?.qbo_expense_account_name ?? saved?.qbo_expense_account_name ?? null;
  const gipiRows = draftRows.filter((line) => line.kind === "gipi");
  const expenseRows = draftRows.filter((line) => line.kind === "expense");
  const allocated = draftRows.reduce((sum, line) => sum + Math.round(money(line.amount) * 100), 0);
  const target = Math.round(money(totalText) * 100);
  const remaining = (target - allocated) / 100;

  function rememberUpsResult(
    result: { invoice: ApInvoiceRow; ups: UpsInvoiceState },
    notifyParent: boolean,
  ) {
    queryClient.setQueryData(["accounting", "ap-invoice-ups", props.invoiceId], result.ups);
    queryClient.setQueryData(["accounting", "ap-invoice", props.invoiceId], result.invoice);
    if (notifyParent) {
      props.onInvoiceUpdated(result.invoice);
    }
    setInvoiceTotal(null);
    setRows(null);
    setCategoryId(null);
    setCategoryName(null);
  }

  const allocationRef = useRef({
    rows: draftRows,
    accountId,
    accountName,
    totalText,
  });
  allocationRef.current = { rows: draftRows, accountId, accountName, totalText };

  function publishRows(next: DraftLine[]) {
    allocationRef.current = { ...allocationRef.current, rows: next };
    setRows(next);
  }

  function publishCategory(id: string, name: string) {
    allocationRef.current = {
      ...allocationRef.current,
      accountId: id !== "" ? id : null,
      accountName: name !== "" ? name : null,
    };
    setCategoryId(id);
    setCategoryName(name);
  }

  const buildAllocationPayload = useCallback(
    () => {
      const current = allocationRef.current;
      const allocated = current.rows.reduce((sum, line) => sum + Math.round(money(line.amount) * 100), 0);
      const target = Math.round(money(current.totalText) * 100);
      const remainderAmount = (target - allocated) / 100;
      return {
      invoice_total: money(current.totalText),
      lines: [
        ...current.rows.map((line) => ({
          description: line.description,
          amount: money(line.amount),
          kind: line.kind,
          purchase_order_id: null,
          purchase_invoice_id:
            line.kind === "gipi" && line.purchaseInvoiceId !== ""
              ? Number.parseInt(line.purchaseInvoiceId, 10)
              : null,
          qbo_expense_account_id: line.kind === "expense" ? line.categoryId || null : null,
          qbo_expense_account_name: line.kind === "expense" ? line.categoryName || null : null,
        })),
        ...(remainderAmount > 0
          ? [
              {
                description: "All other items",
                amount: remainderAmount,
                kind: "remainder" as const,
                purchase_order_id: null,
                purchase_invoice_id: null,
                qbo_expense_account_id: current.accountId,
                qbo_expense_account_name: current.accountName,
              },
            ]
          : []),
      ],
      qbo_expense_account_id: remainderAmount > 0 ? current.accountId : null,
      qbo_expense_account_name: remainderAmount > 0 ? current.accountName : null,
    };
    },
    [],
  );

  const saveQueueRef = useRef(createApInvoiceUpsSaveQueue());
  const continueQueuedSaveRef = useRef<() => void>(() => {});
  const saveMutation = useMutation({
    mutationFn: () => saveApInvoiceUpsAllocation(props.invoiceId, buildAllocationPayload()),
    onSuccess: (result) => {
      if (settleApInvoiceUpsSave(saveQueueRef.current) === "again") {
        queueMicrotask(() => continueQueuedSaveRef.current());
        return;
      }
      rememberUpsResult(result, false);
    },
    onError: (saveError: unknown) => {
      if (settleApInvoiceUpsSave(saveQueueRef.current) === "again") {
        queueMicrotask(() => continueQueuedSaveRef.current());
        return;
      }
      showToast({
        tone: "error",
        message: saveError instanceof Error ? saveError.message : "Could not save the lines.",
      });
    },
  });

  continueQueuedSaveRef.current = () => {
    saveMutation.mutate();
  };

  const saveAllocationOnBlur = useCallback(() => {
    if (!upsQuery.isSuccess || applied || props.formLocked) return;
    if (requestApInvoiceUpsSave(saveQueueRef.current) === "defer") return;
    saveMutation.mutate();
  }, [applied, props.formLocked, saveMutation, upsQuery.isSuccess]);

  const saveAllocationAfterCommit = useCallback(() => {
    queueMicrotask(() => saveAllocationOnBlur());
  }, [saveAllocationOnBlur]);

  useEffect(() => {
    props.submitRef.current = async () => {
      await saveApInvoiceUpsAllocation(props.invoiceId, buildAllocationPayload());
      const appliedResult = await applyApInvoiceUpsAllocation(props.invoiceId);
      rememberUpsResult(appliedResult, true);
      return appliedResult.invoice;
    };
    return () => {
      props.submitRef.current = null;
    };
  });

  const locked = props.formLocked || applied;

  function updateRow(key: string, patch: Partial<DraftLine>) {
    publishRows(allocationRef.current.rows.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint={LINE_HELP}>Multi-PO TSH</CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="max-w-xs">
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="multi-po-tsh-total">
              Invoice total
            </label>
            <Input
              id="multi-po-tsh-total"
              type="number"
              min="0"
              step="0.01"
              value={totalText}
              disabled={locked}
              onChange={(event) => setInvoiceTotal(event.target.value)}
              onBlur={saveAllocationOnBlur}
            />
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-800">Purchase order lines</p>
          {gipiRows.length === 0 ? (
            <p className="text-sm text-slate-500">No purchase-order lines. Search a GIPI by purchase order when freight belongs on one.</p>
          ) : (
            <ul className="space-y-3">
              {gipiRows.map((line) => (
                <li
                  key={line.key}
                  className="grid gap-2 rounded-md border border-slate-200 p-3 md:grid-cols-[minmax(0,1.2fr)_8rem_minmax(0,1.3fr)_auto] md:items-end"
                >
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Description</label>
                    <Input
                      value={line.description}
                      disabled={locked}
                      aria-label="Line description"
                      onChange={(event) => updateRow(line.key, { description: event.target.value })}
                      onBlur={saveAllocationOnBlur}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Amount</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.amount}
                      disabled={locked}
                      aria-label="Line amount"
                      onChange={(event) => updateRow(line.key, { amount: event.target.value })}
                      onBlur={saveAllocationOnBlur}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">GIPI</label>
                    <LineGipiField
                      purchaseInvoiceId={line.purchaseInvoiceId}
                      purchaseInvoiceLabel={line.purchaseInvoiceLabel}
                      disabled={locked}
                      onChange={(purchaseInvoiceId, label) =>
                        updateRow(line.key, { purchaseInvoiceId, purchaseInvoiceLabel: label })
                      }
                      onCommit={saveAllocationAfterCommit}
                    />
                    {line.purchaseInvoiceId === "" && line.purchaseOrderHint !== "" ? (
                      <p className="mt-1 text-xs text-slate-500">Search purchase order {line.purchaseOrderHint}.</p>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="self-end"
                    disabled={locked}
                    onClick={() => {
                      publishRows(allocationRef.current.rows.filter((candidate) => candidate.key !== line.key));
                      saveAllocationAfterCommit();
                    }}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {applied ? null : (
            <Button
              type="button"
              variant="secondary"
              disabled={locked}
              onClick={() =>
                publishRows([
                  ...allocationRef.current.rows,
                  {
                    key: `gipi-${Date.now()}`,
                    kind: "gipi",
                    description: "",
                    amount: "",
                    purchaseInvoiceId: "",
                    purchaseInvoiceLabel: "",
                    purchaseOrderHint: "",
                    categoryId: "",
                    categoryName: "",
                    createdInvoiceId: null,
                  },
                ])
              }
            >
              Add purchase order line
            </Button>
          )}
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-800">Non-PO items</p>
          {expenseRows.length === 0 ? (
            <p className="text-sm text-slate-500">No named non-PO items. Each one needs its own category.</p>
          ) : (
            <ul className="space-y-3">
              {expenseRows.map((line) => (
                <li key={line.key} className="space-y-2 rounded-md border border-slate-200 p-3">
                  <div className="grid gap-2 md:grid-cols-[minmax(0,1.2fr)_8rem_auto] md:items-end">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Description</label>
                      <Input
                        value={line.description}
                        disabled={locked}
                        aria-label="Line description"
                        onChange={(event) => updateRow(line.key, { description: event.target.value })}
                        onBlur={saveAllocationOnBlur}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Amount</label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.amount}
                        disabled={locked}
                        aria-label="Line amount"
                        onChange={(event) => updateRow(line.key, { amount: event.target.value })}
                        onBlur={saveAllocationOnBlur}
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="self-end"
                      disabled={locked}
                      onClick={() => {
                        publishRows(allocationRef.current.rows.filter((candidate) => candidate.key !== line.key));
                        saveAllocationAfterCommit();
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                  <ApInvoiceExpenseCategoryField
                    lineKey={line.key}
                    accountId={line.categoryId || null}
                    accountName={line.categoryName || null}
                    disabled={locked}
                    onChange={(account) => {
                      updateRow(line.key, {
                        categoryId: account?.id ?? "",
                        categoryName: account?.name ?? "",
                      });
                      saveAllocationAfterCommit();
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
          {applied ? null : (
            <Button
              type="button"
              variant="secondary"
              disabled={locked}
              onClick={() =>
                publishRows([
                  ...allocationRef.current.rows,
                  {
                    key: `expense-${Date.now()}`,
                    kind: "expense",
                    description: "",
                    amount: "",
                    purchaseInvoiceId: "",
                    purchaseInvoiceLabel: "",
                    purchaseOrderHint: "",
                    categoryId: "",
                    categoryName: "",
                    createdInvoiceId: null,
                  },
                ])
              }
            >
              Add non-PO item
            </Button>
          )}
        </div>
        <div className="space-y-2 rounded-md border border-slate-200 p-3">
          <p className="text-sm font-medium text-slate-800">All other items</p>
          <p className="text-sm text-slate-700">
            Amount{" "}
            {remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          {remaining < 0 ? (
            <p className="text-sm text-rose-700">Line amounts cannot be more than the invoice total.</p>
          ) : remaining === 0 ? (
            <p className="text-sm text-slate-600">Nothing left. This line is not needed.</p>
          ) : (
            <ApInvoiceExpenseCategoryField
              lineKey="multi-po-tsh-remainder"
              label="Category for all other items"
              hoverHint="QuickBooks expense account for whatever is left after the purchase-order lines and named non-PO items. Freight Out is the usual choice."
              accountId={accountId}
              accountName={accountName}
              disabled={locked}
              onChange={(account) => {
                publishCategory(account?.id ?? "", account?.name ?? "");
                saveAllocationAfterCommit();
              }}
            />
          )}
        </div>
        {(saved?.created_tsh.length ?? 0) > 0 ? (
          <div className="space-y-1">
            <p className="text-sm font-medium text-slate-800">TSH lines on this invoice</p>
            <ul className="space-y-1 text-sm">
              {saved?.created_tsh.map((row) => (
                <li key={`${row.purchase_order_id ?? "po"}-${row.created_accounts_payable_invoice_id ?? row.amount}`}>
                  <span className="font-medium text-slate-900">
                    {row.purchase_order_number?.trim() || "Purchase order"} — {row.amount}
                  </span>
                  {row.mv_number != null ? <span className="text-slate-600"> for {row.mv_number}</span> : null}
                  {row.created_accounts_payable_invoice_id != null ? (
                    <>
                      {" "}
                      <Link
                        className="text-brand-700 hover:underline"
                        to={`/accounting/ap/${row.created_accounts_payable_invoice_id}/edit`}
                      >
                        Open linked invoice
                      </Link>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {applied ? (
          <p className="text-sm text-slate-600">
            Submitted as one Multi-PO TSH invoice. Purchase-order TSH lines and non-PO lines stay on this vendor invoice.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
